import { ArrowRightOnRectangleIcon, UserPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Avatar, Button } from '@heroui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsPeople } from "react-icons/bs";
import { MdOutlineModeEditOutline } from "react-icons/md";
import { toast } from '../../utils/toast';
import { AddPeoplePopover } from '../../pages/chat/addPeoplePopover';
import { removeUserFromChatRoom } from '../../services/api';
import { ProfileId } from '../../types/types';

interface ChatHeaderProps {
    selectedUser: any;
    selectedGroup: any;
    fetchingMessages: boolean;
    showGroupInfoModal: boolean;
    setShowGroupInfoModal: React.Dispatch<React.SetStateAction<boolean>>;
    groupInfoRef: React.RefObject<HTMLDivElement>;
    isAddMemberOpen: boolean;
    setIsAddMemberOpen: React.Dispatch<React.SetStateAction<boolean>>;
    handleAddPeople: (memberIds: ProfileId[], groupId: number) => Promise<void>;
    getAllMembers: (params: { search: string; limit: number; excludeGroupMembers?: number }) => Promise<any>;
    handleRefreshFlag: () => void;
    showRemoveConfirm: boolean;
    setShowRemoveConfirm: React.Dispatch<React.SetStateAction<boolean>>;
    memberToRemove: ProfileId | null;
    setMemberToRemove: React.Dispatch<React.SetStateAction<ProfileId | null>>;
    removeUserFromChatRoom?: (groupId: number, userId: ProfileId) => null;
    fetchGroupMessages: (groupId: number) => Promise<void>;
    loggedInUserName: string;
    showLeaveConfirm: boolean;
    setShowLeaveConfirm: React.Dispatch<React.SetStateAction<boolean>>;
    setShowGroupModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedGroup: React.Dispatch<React.SetStateAction<any>>;
    handleLeaveGroup: () => Promise<void>;
    isLeaving: boolean;
    setIsLeaving: React.Dispatch<React.SetStateAction<boolean>>;
    selectedChatType: string;
    onViewProfile: (profileId?: string | number | null) => void;
}

export const ChatHeader: React.FC<ChatHeaderProps> = ({
    selectedUser,
    selectedGroup,
    fetchingMessages,
    showGroupInfoModal,
    setShowGroupInfoModal,
    groupInfoRef,
    isAddMemberOpen,
    setIsAddMemberOpen,
    handleAddPeople,
    getAllMembers,
    handleRefreshFlag,
    showRemoveConfirm,
    setShowRemoveConfirm,
    memberToRemove,
    setMemberToRemove,
    fetchGroupMessages,
    loggedInUserName,
    showLeaveConfirm,
    setShowLeaveConfirm,
    setShowGroupModal,
    setSelectedGroup,
    handleLeaveGroup,
    isLeaving,
    selectedChatType,
    onViewProfile,
}) => {
    const { t } = useTranslation('chat');

    return (
        <>
            <div className="border-b border-gray-100 h-16 flex items-center px-6 bg-white shrink-0">
                <div className="flex items-center gap-3 w-full">
                    {selectedUser?.profileDto.profileImage || selectedGroup?.profileImage ? (
                        selectedUser ? (
                            <button
                                type="button"
                                onClick={() => onViewProfile(selectedUser.profileDto.id)}
                                className="rounded-full outline-none focus:outline-none focus:ring-0 shrink-0"
                            >
                                <Avatar
                                    src={selectedUser.profileDto.profileImage}
                                    name={`${selectedUser.profileDto.firstName} ${selectedUser.profileDto.lastName}`}
                                    className="w-10 h-10 text-sm"
                                />
                            </button>
                        ) : (
                            <Avatar
                                src={selectedGroup?.profileImage}
                                name={selectedGroup?.name}
                                className="w-10 h-10 text-sm shrink-0"
                            />
                        )
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 text-lg shrink-0">
                            <BsPeople />
                        </div>
                    )}

                    <div
                        className={`flex-1 min-w-0 ${!selectedUser ? 'cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded-xl transition-colors' : ''}`}
                        onClick={!selectedUser ? () => setShowGroupInfoModal(true) : undefined}
                    >
                        <h2 className="text-base font-medium font-['Plus_Jakarta_Sans'] text-start text-gray-900 truncate leading-6">
                            {selectedUser ? (
                                <button
                                    type="button"
                                    onClick={() => onViewProfile(selectedUser.profileDto.id)}
                                    className="hover:text-blue-600 outline-none focus:outline-none focus:ring-0 text-start"
                                >
                                    {selectedUser.profileDto.firstName} {selectedUser.profileDto.lastName}
                                </button>
                            ) : selectedGroup?.name}
                        </h2>
                        <p className="text-sm font-normal font-['Plus_Jakarta_Sans'] text-start truncate leading-5" style={{ color: 'var(--colors-green-600, #16A34A)' }}>
                            {selectedUser
                                ? (selectedUser.profileDto.email || t('online', 'Online'))
                                : selectedGroup?.bio || t('groupChat', 'Group chat')}
                        </p>
                    </div>

                    {fetchingMessages && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                            <span>{t('updating', 'Updating...')}</span>
                        </div>
                    )}

                    {!selectedUser && (
                        <div className="flex items-center gap-2 ml-2 relative">
                            <button
                                onClick={() => setShowGroupInfoModal(true)}
                                title={t('groupInfo', 'Group Info')}
                                className="flex items-center gap-1 text-gray-600 hover:text-blue-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
                            >
                                <UserPlusIcon className="h-5 w-5" />
                                <span className="text-sm font-medium font-['Plus_Jakarta_Sans']">
                                    {selectedGroup?.members?.length ?? 0}
                                </span>
                            </button>

                            {showGroupInfoModal && selectedGroup && (
                                <div
                                    ref={groupInfoRef}
                                    className="absolute top-full right-0 mt-2 min-w-72 max-w-sm z-50 bg-white shadow-xl rounded-2xl p-4 border border-gray-100 overflow-x-auto"
                                >
                                    <div className="max-h-48 overflow-y-auto space-y-2">
                                        {selectedGroup.members?.length > 0 ? (
                                            selectedGroup.members.map((member: any) => (
                                                <div key={member.id} className="flex items-center justify-between group p-1 rounded-xl hover:bg-gray-50">
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            type="button"
                                                            onClick={() => onViewProfile(member.id)}
                                                            className="rounded-full outline-none focus:outline-none focus:ring-0"
                                                        >
                                                            <Avatar
                                                                src={member.profileImage}
                                                                name={`${member.firstName} ${member.lastName}`}
                                                                className="w-8 h-8 text-xs"
                                                            />
                                                        </button>
                                                        <div className="flex flex-col text-left">
                                                            <button
                                                                type="button"
                                                                onClick={() => onViewProfile(member.id)}
                                                                className="text-sm font-medium font-['Plus_Jakarta_Sans'] text-gray-800 text-left hover:text-blue-600 outline-none focus:outline-none focus:ring-0"
                                                            >
                                                                {member.firstName} {member.lastName}
                                                            </button>
                                                            <p className="text-xs text-gray-400 text-left">{member.email}</p>
                                                        </div>
                                                    </div>
                                                    {selectedGroup.createdBy?.email === loggedInUserName &&
                                                        member.email !== loggedInUserName && (
                                                            <Button
                                                                onClick={() => {
                                                                    setMemberToRemove(member.id);
                                                                    setShowRemoveConfirm(true);
                                                                }}
                                                                isIconOnly
                                                                size="sm"
                                                                className="text-red-600 hover:text-red-800 hidden group-hover:inline-flex"
                                                            >
                                                                <XMarkIcon className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-500">{t('noMembersFound', 'No members found')}</p>
                                        )}
                                    </div>

                                    <div className="mt-4 space-y-2">
                                        {selectedGroup.createdBy.email === loggedInUserName && (
                                            <Button
                                                size="sm"
                                                className="w-full justify-center rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-800 font-medium"
                                                onClick={() => setIsAddMemberOpen(true)}
                                            >
                                                <UserPlusIcon className="w-4 h-4 mr-1" />
                                                {t('addMembers', 'Add members')}
                                            </Button>
                                        )}

                                        {selectedGroup.createdBy.email !== loggedInUserName && (
                                            <Button
                                                onClick={() => setShowLeaveConfirm(true)}
                                                size="sm"
                                                className="w-full justify-center rounded-2xl"
                                                variant="flat"
                                                color="danger"
                                            >
                                                <ArrowRightOnRectangleIcon className="w-4 h-4 mr-1" />
                                                {t('leaveGroup', 'Leave group')}
                                            </Button>
                                        )}
                                    </div>

                                    {showRemoveConfirm && (
                                        <div
                                            className="fixed inset-0 flex items-center justify-center bg-black/40 z-50"
                                            role="dialog"
                                            aria-modal="true"
                                        >
                                            <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl border">
                                                <h2 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-2">
                                                    {t("confirmRemoval", "Confirm Removal")}
                                                </h2>
                                                <p className="text-sm text-gray-500 mb-6">
                                                    {t("confirmRemovalMessage", "Are you sure you want to remove this member?")}
                                                </p>
                                                <div className="flex justify-end space-x-3">
                                                    <button
                                                        onClick={() => setShowRemoveConfirm(false)}
                                                        className="px-4 py-2 rounded-2xl bg-gray-100 hover:bg-gray-200 text-sm font-medium text-gray-700"
                                                    >
                                                        {t("cancel", "Cancel")}
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (!memberToRemove) return;
                                                            try {
                                                                await removeUserFromChatRoom(selectedGroup.id, memberToRemove);
                                                                toast.success(t("memberRemovedSuccess", "Member removed successfully"));
                                                                setShowRemoveConfirm(false);
                                                                setMemberToRemove(null);
                                                                handleRefreshFlag();
                                                                fetchGroupMessages(selectedGroup.id);
                                                            } catch (error: any) {
                                                                toast.error(error?.response?.data?.message || t("failedToRemoveMember", "Failed to remove member"));
                                                            }
                                                        }}
                                                        className="px-4 py-2 rounded-2xl bg-red-600 text-white hover:bg-red-700 text-sm font-medium"
                                                    >
                                                        {t("confirm", "Confirm")}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!selectedUser && selectedGroup?.createdBy?.email === loggedInUserName && (
                        <button
                            onClick={() => {
                                setSelectedGroup(selectedGroup);
                                setShowGroupModal(true);
                            }}
                            title={t('editButton', 'Edit')}
                            className="text-gray-500 hover:text-blue-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
                        >
                            <MdOutlineModeEditOutline className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            {isAddMemberOpen && selectedGroup && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-50">
                    <AddPeoplePopover
                        isOpen={isAddMemberOpen}
                        onClose={() => {
                            handleRefreshFlag();
                            setIsAddMemberOpen(false);
                        }}
                        onAddPeople={(memberIds) => handleAddPeople(memberIds, selectedGroup.id)}
                        getAllMembers={getAllMembers}
                        selectedGroup={selectedGroup}
                        onRefresh={handleRefreshFlag}
                        onViewProfile={onViewProfile}
                    />
                </div>
            )}

            {showLeaveConfirm && (
                <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">
                    <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-sm w-full">
                        <h3 className="text-lg font-semibold font-['Plus_Jakarta_Sans'] mb-2">
                            {t('areYouSureLeaveGroup', 'Are you sure?')}
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                            {t('leaveGroupWarning', 'You will leave this group.')}
                        </p>
                        <div className="flex justify-end space-x-3">
                            <Button
                                onClick={() => setShowLeaveConfirm(false)}
                                size="sm"
                                variant="flat"
                                className="rounded-2xl"
                                disabled={isLeaving}
                            >
                                {t('cancel', 'Cancel')}
                            </Button>
                            <Button
                                onClick={handleLeaveGroup}
                                size="sm"
                                color="danger"
                                className="rounded-2xl"
                                disabled={isLeaving}
                            >
                                {isLeaving ? t('leaving', 'Leaving...') : t('leave', 'Leave')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

